import { type FC } from "react";

import { t } from "i18n/i18n";
import { Menu, MenuHeader, MenuItem } from "mds/components/Menu";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { ChevronDownIcon, PersonGroupIcon, PersonSingleIcon, ViewPersonIcon } from "mds/icons";
import {
  selectPageGroupIdsByPage,
  selectPresentClassUserIdsByPageGroupId,
} from "store/selectors/page-groups";
import { useListSelector } from "store/store-hooks";
import { usePinParticipantTools } from "utils/hooks/use-virtual-participant-tools";

interface ViewGroupMenuProps {
  pageId: string;
}

interface ViewGroupMenuItemProps {
  pageGroupId: string;
  groupNumber: number;
  onPinClassUserIdList: (classUserIds: string[]) => void;
}

const ViewGroupMenuItem: FC<ViewGroupMenuItemProps> = ({
  pageGroupId,
  groupNumber,
  onPinClassUserIdList,
}) => {
  const presentClassUserIds = useListSelector((state) =>
    selectPresentClassUserIdsByPageGroupId(state, pageGroupId),
  );

  return (
    <MenuItem onClick={() => onPinClassUserIdList(presentClassUserIds)}>
      <div className="flex w-full min-w-[230px] items-center gap-2">
        <div className="flex h-6 w-6 flex-none items-center justify-center rounded-full bg-hibiscus-tint-95">
          <PersonGroupIcon className="text-hibiscus" size="xs" />
        </div>
        <span className="body-s min-w-0 flex-1 truncate">
          {t("common.group_title", { number: groupNumber })}
        </span>
        <span className="flex w-10 flex-none items-center gap-1 text-black-tint-40">
          <PersonSingleIcon className="flex-none" />
          <span className="body-s w-[2ch] flex-none text-right tabular-nums">
            {presentClassUserIds.length}
          </span>
        </span>
      </div>
    </MenuItem>
  );
};

export const ViewGroupMenu: FC<ViewGroupMenuProps> = ({ pageId }) => {
  const pageGroupIds = useListSelector((state) => selectPageGroupIdsByPage(state, pageId));
  const { onPinClassUserIdList } = usePinParticipantTools();

  const buttonLabel = t("group_configuration_card.view_group");

  const menuButton = (
    <SquareButton aria-label={buttonLabel} kind="tertiary" size="s" title={buttonLabel}>
      <ViewPersonIcon />
      {buttonLabel}
      <ChevronDownIcon />
    </SquareButton>
  );

  return (
    <Menu align="end" menuButton={menuButton}>
      <MenuHeader className="text-xs text-black-tint-55">
        {t("group_configuration_card.select_group_to_view_participants")}
      </MenuHeader>
      {pageGroupIds.map((pageGroupId, index) => (
        <ViewGroupMenuItem
          groupNumber={index + 1}
          key={pageGroupId}
          pageGroupId={pageGroupId}
          onPinClassUserIdList={onPinClassUserIdList}
        />
      ))}
    </Menu>
  );
};
